import type { SegmentDecision } from './vacation.types';

export type SegmentDecisionSource = 'client' | 'dp';

type DecisionMap = Record<string, SegmentDecision> | null | undefined;

interface DecisionFields {
    dpSegmentDecisions?: DecisionMap;
    clientSegmentDecisions?: DecisionMap;
}

interface TimestampLike {
    toMillis?: () => number;
}

function decisionTime(decision: SegmentDecision): number {
    return (decision.at as TimestampLike | null | undefined)?.toMillis?.() ?? 0;
}

export function resolveSegmentDecisionSource(
    request: DecisionFields,
    segmentOrder: number
): { decision: SegmentDecision; source: SegmentDecisionSource } | undefined {
    const key = String(segmentOrder);
    const client = request.clientSegmentDecisions?.[key];
    const dp = request.dpSegmentDecisions?.[key];

    if (client && (!dp || decisionTime(client) >= decisionTime(dp))) {
        return { decision: client, source: 'client' };
    }
    if (dp) {
        return { decision: dp, source: 'dp' };
    }
    return undefined;
}

export function resolveSegmentDecision(
    request: DecisionFields,
    segmentOrder: number
): SegmentDecision | undefined {
    return resolveSegmentDecisionSource(request, segmentOrder)?.decision;
}

export type SegmentLockReason = 'dp' | 'client';

export function resolveClientLockReason(
    segmentOrder: number,
    clientDecisions: DecisionMap,
    dpDecisions: DecisionMap
): SegmentLockReason | null {
    const key = String(segmentOrder);
    const dpStatus = dpDecisions?.[key]?.status;
    const clientStatus = clientDecisions?.[key]?.status;
    if (dpStatus === 'Approved') return 'dp';
    if (clientStatus === 'Approved' && dpStatus !== 'Rejected') return 'client';
    return null;
}

export function isSegmentLockedForClient(
    segmentOrder: number,
    clientDecisions: DecisionMap,
    dpDecisions: DecisionMap
): boolean {
    return resolveClientLockReason(segmentOrder, clientDecisions, dpDecisions) !== null;
}

export function findLockedSegmentOrders(
    segments: Array<{ order: number }>,
    clientDecisions: DecisionMap,
    dpDecisions: DecisionMap
): number[] {
    return segments
        .map((segment) => segment.order)
        .filter((order) => isSegmentLockedForClient(order, clientDecisions, dpDecisions));
}

export function buildSegmentLockReasons(
    segments: Array<{ order: number }>,
    clientDecisions: DecisionMap,
    dpDecisions: DecisionMap
): Record<number, SegmentLockReason> {
    const reasons: Record<number, SegmentLockReason> = {};
    segments.forEach((segment) => {
        const reason = resolveClientLockReason(segment.order, clientDecisions, dpDecisions);
        if (reason) reasons[segment.order] = reason;
    });
    return reasons;
}

export function findRejectedSegmentOrders(decisions: DecisionMap): number[] {
    return Object.entries(decisions ?? {})
        .filter(([, decision]) => decision?.status === 'Rejected')
        .map(([key]) => Number(key));
}

export function latestRejectionTime(request: DecisionFields): number {
    return Math.max(
        0,
        ...[...Object.values(request.dpSegmentDecisions ?? {}), ...Object.values(request.clientSegmentDecisions ?? {})]
            .filter((decision) => decision?.status === 'Rejected')
            .map(decisionTime)
    );
}

